/**
 * Compliance Report PDF Button
 * Triggers generation of detailed compliance PDF reports
 * Supports both single report and combined reports (604 toilets + 404 doors)
 */

import React from 'react';
import { X } from 'lucide-react';
import { ComplianceReportPDF } from '@/services/reports/ComplianceReportPDF';
import { useViewerStore } from '@/store/viewerStore';
import { getModelConfigForJurisdiction, getModelReportFilesForJurisdiction, getModelRegulationDefinitionFiles } from '@/config/models';

interface ComplianceReportButtonProps {
  jsonPath?: string;
  className?: string;
  label?: string;
  /** If true, generates a combined report from all 604 and 404 compliance reports */
  useCombinedReport?: boolean;
}

type ReportType = 'all' | 'problematic';

export const ComplianceReportButton: React.FC<ComplianceReportButtonProps> = ({
  jsonPath,
  className = '',
  label = 'Download PDF Report',
  useCombinedReport = true,
}) => {
  const { selectedModelId, selectedJurisdiction } = useViewerStore();
  const reportFiles = getModelReportFilesForJurisdiction(selectedModelId, selectedJurisdiction).map(f => `/resources/${f}`);
  const defaultJsonPath = reportFiles[0] || '';

  const [isGenerating, setIsGenerating] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [progressMessage, setProgressMessage] = React.useState('');
  const [showDialog, setShowDialog] = React.useState(false);

  const handleGenerateReport = async (reportType: ReportType) => {
    setShowDialog(false);
    setIsGenerating(true);
    setProgress(0);
    setProgressMessage('Starting...');

    try {
      if (useCombinedReport && reportFiles.length > 0) {
        // Generate combined report from all compliance reports for the selected model
        const modelConfig = getModelConfigForJurisdiction(selectedModelId, selectedJurisdiction);
        const regDefFiles = getModelRegulationDefinitionFiles(selectedModelId, selectedJurisdiction);
        await ComplianceReportPDF.generateFromMultipleJSON(
          reportFiles,
          reportType,
          (prog, msg) => {
            setProgress(prog);
            setProgressMessage(msg);
          },
          modelConfig?.projectName,
          undefined, // scopeLabel — auto-derived
          modelConfig?.defaultLevel,
          selectedJurisdiction || modelConfig?.jurisdiction,
          modelConfig?.modelName,
          regDefFiles,
          modelConfig?.coverLogoPath,
          modelConfig?.name,
          modelConfig?.coverImagePath,
        );
      } else {
        // Generate single report from specified JSON path
        await ComplianceReportPDF.generateFromJSON(
          jsonPath || defaultJsonPath,
          reportType,
          (prog, msg) => {
            setProgress(prog);
            setProgressMessage(msg);
          }
        );
      }
    } catch (error) {
      console.error('Error generating PDF report:', error);
      alert('Failed to generate PDF report. Please check the console for details.');
    } finally {
      setIsGenerating(false);
      setProgress(0);
      setProgressMessage('');
    }
  };

  return (
    <>
      <div className="relative">
        <button
          onClick={() => setShowDialog(true)}
          disabled={isGenerating}
          className={`px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md transition-all font-medium shadow-sm hover:shadow-md ${className}`}
        >
          {isGenerating ? (
            <span className="whitespace-nowrap">
              {progressMessage} ({progress}%)
            </span>
          ) : (
            label
          )}
        </button>
        {isGenerating && (
          <div className="absolute bottom-0 left-0 h-1 bg-indigo-400 rounded-b-md transition-all duration-300" style={{ width: `${progress}%` }} />
        )}
      </div>

      {/* Report Type Selection Dialog */}
      {showDialog && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowDialog(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Select Report Type
              </h3>
              <button
                onClick={() => setShowDialog(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-6">
              Choose which findings to include in your PDF report:
            </p>

            <div className="space-y-3">
              <button
                onClick={() => handleGenerateReport('problematic')}
                className="w-full text-left p-4 border-2 border-orange-200 rounded-lg hover:border-orange-400 hover:bg-orange-50 transition-all hover:shadow-md group"
              >
                <div className="font-semibold text-gray-900 mb-1 group-hover:text-orange-700">
                  Failures Only
                </div>
                <div className="text-sm text-gray-600">
                  Include only FAIL status findings (recommended for action items)
                </div>
              </button>

              <button
                onClick={() => handleGenerateReport('all')}
                className="w-full text-left p-4 border-2 border-indigo-200 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-all hover:shadow-md group"
              >
                <div className="font-semibold text-gray-900 mb-1 group-hover:text-indigo-700">
                  All Findings
                </div>
                <div className="text-sm text-gray-600">
                  Include all validations (passes, failures, warnings, and insufficient data)
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
